import {
  Alert,
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  MenuItem,
  Stack,
  TextField,
  Typography,
} from "@mui/material";
import { useMutation } from "@tanstack/react-query";
import { useState } from "react";
import type { JSX } from "react";

import type {
  SalesCampaign,
  SalesCampaignVertical,
} from "@xop/dashboard-shared";

import { MultiSelect } from "@/components/ui/multi-select";
import { salesApi } from "../client.js";
import { describeAgentError } from "../describeAgentError.js";

/**
 * Create / edit a harvest campaign.
 *
 * Search queries are OPTIONAL: the harvester works out how to search each
 * (vertical, region) cell itself, so an empty box means "you decide" — the
 * normal case. Anything typed here is added to what it comes up with, never a
 * cap on it.
 *
 * Editing sends only CHANGED fields (PATCH semantics). Sending a whole object
 * built from form defaults would blank `verticals` on any field the form
 * didn't load, which stops the sweep.
 *
 * A failed save is unwrapped by `describeAgentError` — the dashboard proxy
 * wraps every upstream failure as "Sales agent returned 422" and nests the
 * real message under `details.upstream_body`; showing only the wrapper is why
 * an invalid campaign used to read as an unfixable dead end (the agent was
 * saying "ends_on is before starts_on" and the operator saw a status code).
 */
export function CampaignEditor({
  campaign,
  availableRegions,
  onClose,
  onSaved,
}: {
  campaign: SalesCampaign | null;
  availableRegions: string[];
  onClose: () => void;
  onSaved: () => void;
}): JSX.Element {
  const isEdit = campaign !== null;
  const [label, setLabel] = useState(campaign?.label ?? "");
  const [goal, setGoal] = useState(campaign?.goal ?? "");
  const [status, setStatus] = useState<SalesCampaign["status"]>(
    campaign?.status ?? "draft",
  );
  const [startsOn, setStartsOn] = useState(campaign?.starts_on ?? "");
  const [endsOn, setEndsOn] = useState(campaign?.ends_on ?? "");
  const [regions, setRegions] = useState<string[]>(campaign?.regions ?? []);
  const [verticals, setVerticals] = useState<SalesCampaignVertical[]>(
    campaign?.verticals?.length
      ? campaign.verticals
      : [{ vertical: "", label: "", enumeration_queries: [""], registry_urls: [] }],
  );
  const [error, setError] = useState<string | null>(null);

  const save = useMutation({
    mutationFn: async () => {
      setError(null); // never show a stale reason from the previous attempt
      const payload = {
        label: label.trim(),
        goal: goal.trim(),
        status,
        starts_on: startsOn || null,
        ends_on: endsOn || null,
        regions,
        verticals: verticals
          .filter((v) => (v.label || v.vertical).trim())
          .map((v) => ({
            // Keep the EXISTING slug when there is one. It is the join key on
            // every harvested org, contact and mission, so deriving it from the
            // display name again would orphan the campaign's whole history the
            // first time someone tidies a label.
            vertical: (v.vertical || v.label).trim(),
            label: (v.label || v.vertical).trim(),
            enumeration_queries: v.enumeration_queries
              .map((q) => q.trim())
              .filter(Boolean),
            registry_urls: v.registry_urls.map((u) => u.trim()).filter(Boolean),
          })),
      };
      return isEdit
        ? salesApi.updateCampaign(campaign.slug, payload)
        : salesApi.createCampaign(payload);
    },
    onSuccess: onSaved,
    onError: (e: Error) => setError(describeAgentError(e, "Could not save")),
  });

  const patchVertical = (i: number, patch: Partial<SalesCampaignVertical>) =>
    setVerticals((vs) => vs.map((v, n) => (n === i ? { ...v, ...patch } : v)));

  return (
    <Dialog open onClose={onClose} maxWidth="md" fullWidth>
      <DialogTitle>{isEdit ? `Edit “${campaign.label}”` : "New campaign"}</DialogTitle>
      <DialogContent dividers>
        <Stack spacing={2.5} sx={{ pt: 1 }}>
          {error && <Alert severity="error">{error}</Alert>}

          <TextField
            label="Name"
            value={label}
            onChange={(e) => setLabel(e.target.value)}
            placeholder="August — Banking & Trading"
            fullWidth
            required
          />
          <TextField
            label="Goal"
            value={goal}
            onChange={(e) => setGoal(e.target.value)}
            placeholder="Collect the most named decision-maker contacts + emails for the September webinar"
            fullWidth
            multiline
            minRows={2}
            helperText="What success means for this campaign. Shown on its results page."
          />

          <Stack direction={{ xs: "column", sm: "row" }} spacing={2}>
            <TextField
              select
              label="Status"
              value={status}
              onChange={(e) => setStatus(e.target.value as SalesCampaign["status"])}
              sx={{ minWidth: 180 }}
              helperText={
                status === "active"
                  ? "Live: the harvester sweeps ONLY this"
                  : "Draft/Archived: not swept"
              }
            >
              <MenuItem value="draft">Draft</MenuItem>
              <MenuItem value="active">Active</MenuItem>
              <MenuItem value="archived">Archived</MenuItem>
            </TextField>
            <TextField
              label="Starts on"
              type="date"
              value={startsOn}
              onChange={(e) => setStartsOn(e.target.value)}
              InputLabelProps={{ shrink: true }}
              fullWidth
            />
            <TextField
              label="Ends on"
              type="date"
              value={endsOn}
              onChange={(e) => setEndsOn(e.target.value)}
              InputLabelProps={{ shrink: true }}
              fullWidth
              helperText="After this date the sweep reverts to the default catalog"
            />
          </Stack>

          <Box>
            <Typography sx={{ fontWeight: 600, mb: 0.5 }}>Regions</Typography>
            <Typography sx={{ color: "text.secondary", fontSize: 13, mb: 1 }}>
              Leave empty to hunt worldwide.
            </Typography>
            <MultiSelect
              options={availableRegions.map((r) => ({ value: r, label: r }))}
              selected={regions}
              onChange={setRegions}
              placeholder="All regions"
            />
          </Box>

          <Box>
            <Typography sx={{ fontWeight: 600, mb: 0.5 }}>Verticals</Typography>
            <Typography sx={{ color: "text.secondary", fontSize: 13, mb: 1.5 }}>
              The sectors to hunt. Name them — the harvester works out how to
              search each sector in each region on its own, so you don’t have
              to write queries.
            </Typography>

            <Stack spacing={2}>
              {verticals.map((v, i) => (
                <Box
                  key={i}
                  sx={{ border: 1, borderColor: "divider", borderRadius: 1, p: 2 }}
                >
                  <Stack direction="row" gap={1} alignItems="flex-start">
                    <TextField
                      label="Vertical name"
                      value={v.label}
                      onChange={(e) =>
                        patchVertical(i, { label: e.target.value, vertical: e.target.value })
                      }
                      placeholder="Investment banks & trading floors"
                      fullWidth
                      size="small"
                    />
                    {verticals.length > 1 && (
                      <IconButton
                        aria-label="Remove vertical"
                        onClick={() =>
                          setVerticals((vs) => vs.filter((_, n) => n !== i))
                        }
                      >
                        ×
                      </IconButton>
                    )}
                  </Stack>
                  <TextField
                    label="Extra search queries (optional, one per line)"
                    value={v.enumeration_queries.join("\n")}
                    onChange={(e) =>
                      patchVertical(i, { enumeration_queries: e.target.value.split("\n") })
                    }
                    placeholder={
                      "Leave empty — the harvester decides how to search.\n" +
                      "Add a line only to force a search it might not think of."
                    }
                    fullWidth
                    multiline
                    minRows={2}
                    size="small"
                    sx={{ mt: 1.5 }}
                    helperText="Leave empty and the harvester devises its own searches per region. Anything here is added to those, never instead of them. {region} is replaced per region."
                  />
                  <TextField
                    label="Registry pages (optional, one per line)"
                    value={v.registry_urls.join("\n")}
                    onChange={(e) =>
                      patchVertical(i, { registry_urls: e.target.value.split("\n") })
                    }
                    placeholder="https://www.example.org/member-directory"
                    fullWidth
                    multiline
                    minRows={1}
                    size="small"
                    sx={{ mt: 1.5 }}
                    helperText="Authoritative directories fetched alongside the search results — the best source of complete lists."
                  />
                </Box>
              ))}
            </Stack>

            <Button
              size="small"
              sx={{ mt: 1.5 }}
              onClick={() =>
                setVerticals((vs) => [
                  ...vs,
                  { vertical: "", label: "", enumeration_queries: [""], registry_urls: [] },
                ])
              }
            >
              Add vertical
            </Button>
          </Box>
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Cancel</Button>
        <Button
          variant="contained"
          onClick={() => {
            setError(null);
            save.mutate();
          }}
          disabled={save.isPending || !label.trim()}
        >
          {save.isPending ? "Saving…" : isEdit ? "Save changes" : "Create campaign"}
        </Button>
      </DialogActions>
    </Dialog>
  );
}
